import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { ENGINES } from '../mock/mock';
import { Users, ArrowRight, Check } from 'lucide-react';
import ConstellationBackdrop from '../components/ConstellationBackdrop';
import ArclipseBrandLockup from '../components/ArclipseBrandLockup';

// Engine nodes that light up one by one as a micro-animation
const ENGINE_NODES = [
  { key: 'TITAN',     primary: '#B03A2E', light: '#e05252' },
  { key: 'ARBITER',   primary: '#1F618D', light: '#4a9cc4' },
  { key: 'ALCHEMIST', primary: '#6C3483', light: '#9b59b6' },
  { key: 'ARTISAN',   primary: '#148F77', light: '#1abc9c' },
  { key: 'ORACLE',    primary: '#B7770D', light: '#f39c12' },
  { key: 'SOVEREIGN', primary: '#B94A2C', light: '#e67e22' },
];

function EngineNodeAnimation({ topEngine }) {
  const [litCount, setLitCount] = useState(0);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    // Light up nodes one by one
    const timers = ENGINE_NODES.map((_, i) =>
      setTimeout(() => setLitCount(c => c + 1), 300 + i * 220)
    );
    // Start pulse after all nodes are lit
    const pulseTimer = setTimeout(() => setPulsing(true), 300 + ENGINE_NODES.length * 220 + 300);
    return () => { timers.forEach(clearTimeout); clearTimeout(pulseTimer); };
  }, []);

  const topIdx = ENGINE_NODES.findIndex(n => n.key === topEngine);

  return (
    <div className="flex items-center justify-center gap-3 my-6">
      {ENGINE_NODES.map((node, i) => {
        const isTop = i === topIdx;
        const isLit = i < litCount;
        return (
          <div key={node.key} className="flex flex-col items-center gap-1.5">
            <div
              className="rounded-full transition-all duration-500"
              style={{
                width: isTop ? 18 : 12,
                height: isTop ? 18 : 12,
                background: isLit ? node.primary : 'rgba(255,255,255,0.08)',
                boxShadow: isLit
                  ? (isTop && pulsing
                    ? `0 0 0 4px ${node.primary}22, 0 0 20px ${node.primary}cc, 0 0 40px ${node.primary}55`
                    : `0 0 12px ${node.primary}99`)
                  : 'none',
                animation: isTop && pulsing ? 'neonPulse 2.4s ease-in-out infinite' : 'none',
              }}
            />
            <div className="font-data text-[8px] tracking-widest uppercase"
              style={{ color: isLit ? node.light : 'rgba(255,255,255,0.18)', opacity: isLit ? 1 : 0.4 }}>
              {node.key.slice(0, 3)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Animated checkmark SVG
function CheckmarkAnim() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ display: 'block' }}>
      <circle cx="32" cy="32" r="30" stroke="rgba(29,158,117,0.4)" strokeWidth="2" />
      <circle cx="32" cy="32" r="30"
        stroke="#1D9E75" strokeWidth="2"
        strokeDasharray="188.5" strokeDashoffset="188.5"
        fill="none"
        style={{ animation: 'dashFill 0.7s ease-out 0.2s forwards' }}
      />
      <path d="M20 32 L29 41 L44 24"
        stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray="32" strokeDashoffset="32"
        style={{ animation: 'dashFill 0.4s ease-out 0.75s forwards' }}
      />
      <style>{`
        @keyframes dashFill {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </svg>
  );
}

// Animated counter
function AnimCounter({ target }) {
  const [displayed, setDisplayed] = useState(0);

  useEffect(() => {
    if (!target) return;
    const duration = 1200;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) { setDisplayed(target); clearInterval(interval); return; }
      setDisplayed(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(interval);
  }, [target]);

  return <span>{displayed.toLocaleString()}</span>;
}

export default function WaitlistThankYou() {
  const location = useLocation();
  const { user } = useAuth();
  const { result, email, waitlistCount: initialCount, lifeOsPrivate, lifeOsArchived, networkWaitlist } = location.state || {};
  const [count, setCount] = useState(initialCount || null);
  const displayEmail = email || user?.email;

  const hasLifeOsAccess = Boolean(user?.life_os_access) && !lifeOsArchived;

  useEffect(() => {
    api.get('/waitlist/count').then(r => setCount(r.data.count)).catch(() => {
      if (!count) setCount(initialCount || 0);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const eng = result?.top ? ENGINES[result.top] : null;

  return (
    <div className="relative min-h-screen font-body text-white bg-[#050403] flex flex-col overflow-hidden">
      <ConstellationBackdrop variant="success" accent={eng?.primary || '#e4e4e8'} />

      {/* Nav */}
      <header className="relative z-10 px-6 md:px-10 py-5 flex items-center">
        <Link to="/" className="flex items-center gap-3">
          <ArclipseBrandLockup />
        </Link>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-[560px] text-center fade-up">

          {/* Checkmark animation */}
          <div className="flex justify-center mb-6">
            <CheckmarkAnim />
          </div>

          <div className="chip inline-flex mb-4" style={{ borderColor: 'rgba(29,158,117,0.45)', color: '#1D9E75', background: 'rgba(29,158,117,0.08)' }}>
            <Check size={12} /> {hasLifeOsAccess ? "You're in" : "You're on the waitlist"}
          </div>

          <h1 className="font-head text-[32px] md:text-[42px] leading-[1.15] text-white mb-4">
            {hasLifeOsAccess
              ? 'Welcome to Arclipse Constellation.'
              : networkWaitlist
                ? "You're on the Arclipse Constellation waitlist."
                : lifeOsPrivate
                  ? 'Arclipse Constellation is private for now.'
                  : lifeOsArchived
                    ? 'Arclipse Constellation is the active product now.'
                  : 'Your Arc is waiting for you.'}
          </h1>

          {displayEmail && (
            <p className="text-white/55 text-[14px] leading-[1.8] mb-2">
              {hasLifeOsAccess
                ? <>Logged in as <span className="text-white/80">{displayEmail}</span>. Your Constellation profile is connected to this email.</>
                : <>We'll notify <span className="text-white/80">{displayEmail}</span> when Arclipse Constellation access opens.</>
              }
            </p>
          )}

          {result?.arcKey && (
            <p className="text-white/55 text-[14px] leading-[1.8] mb-8">
              Your Arc, <span style={{ color: eng?.light }}>{result.arcKey}</span>, is {hasLifeOsAccess ? 'live' : 'saved to your future Constellation profile'}.
            </p>
          )}

          {/* Engine node animation */}
          {result?.top && <EngineNodeAnimation topEngine={result.top} />}

          {/* Waitlist counter */}
          {count !== null && (
            <div className="panel-strong px-6 py-5 rounded-2xl mb-8 inline-block">
              <div className="flex items-center justify-center gap-3">
                <Users size={18} className="text-white/50" />
                <div>
                  <div className="font-head text-[34px] leading-none" style={{ color: eng?.light || '#C7A46D' }}>
                    <AnimCounter target={count} />
                  </div>
                  <div className="font-data text-[11px] tracking-[0.14em] uppercase text-white/40 mt-0.5">
                    on the Arclipse Constellation waitlist
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Link
              to={hasLifeOsAccess ? '/app' : '/v3'}
              className="constellation-cta constellation-cta--full">
              {hasLifeOsAccess ? 'Enter private workspace' : 'Return to Constellation'} <ArrowRight size={14} />
            </Link>
            <p className="text-[12px] text-white/30">
              {hasLifeOsAccess
                ? 'Your Constellation profile stays connected to your email.'
                : lifeOsArchived
                  ? 'Arclipse Constellation is the active product now.'
                : networkWaitlist
                  ? 'Your ARC signal is saved to your account for Arclipse Constellation review.'
                  : 'Arclipse Constellation is the active product now.'}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
